import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Megaphone,
  Calendar,
  Building2,
  Download,
  Upload,
  CheckCircle,
  Loader2,
  Clock,
  ShieldAlert,
  Linkedin,
  Users,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OpportunityPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, isExpert, loading: authLoading, API: AuthAPI } = useAuth();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/announcements/public/${id}`);
        if (res.ok) {
          const data = await res.json();
          setOpp(data);
        } else if (res.status === 404) {
          setError('Opportunity not found or has been removed.');
        } else {
          setError('Failed to load opportunity.');
        }
      } catch (e) {
        setError('Failed to load opportunity.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // If logged in as expert, check whether they've already submitted
  useEffect(() => {
    if (!isExpert || !token) return;
    (async () => {
      try {
        const res = await fetch(`${AuthAPI || API}/announcements/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const mine = (data.announcements || []).find((a) => a.id === id);
          if (mine?.my_submission) setSubmitted(true);
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [isExpert, token, id, AuthAPI]);

  const daysUntil = (deadline) => {
    if (!deadline) return null;
    const d = deadline.includes('T') ? new Date(deadline) : new Date(deadline + 'T23:59:59Z');
    return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/announcements/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = opp.pdf_filename || 'opportunity.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const submitProposal = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/announcements/${id}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (res.ok) {
        toast.success('Proposal submitted! Confirmation email sent.');
        setSubmitted(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Submission failed');
      }
    } catch (e) {
      toast.error('Submission failed');
    } finally {
      setUploading(false);
    }
  };

  const goSignInAsExpert = () => {
    // Stash this URL so AuthContext can redirect back after LinkedIn auth completes
    try {
      localStorage.setItem('post_auth_redirect', `/opportunities/${id}`);
    } catch (e) { /* noop */ }
    navigate('/expert/signup');
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !opp) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-lg font-semibold mb-2">Opportunity unavailable</h2>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const days = daysUntil(opp.deadline);
  const isExpired = !opp.is_active || (days !== null && days < 0);
  const showExpertGate = !isAuthenticated || !isExpert;
  const isIndustry = isAuthenticated && !isExpert;
  const isApproved = isExpert && user?.is_approved;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <nav className="nav-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
          {isExpert && (
            <Link to="/expert/dashboard">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
            </Link>
          )}
        </div>
      </nav>

      <main className="container-main py-8 sm:py-12 max-w-4xl">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Badge variant="outline" className="gap-1">
            <Megaphone className="w-3 h-3" />
            R&amp;D Opportunity
          </Badge>
          {isExpired ? (
            <Badge variant="secondary">Expired</Badge>
          ) : days !== null && days <= 1 ? (
            <Badge variant="destructive">
              <Clock className="w-3 h-3 mr-1" />
              {days === 0 ? 'Last day!' : 'Tomorrow!'}
            </Badge>
          ) : days !== null ? (
            <Badge variant="secondary">
              <Clock className="w-3 h-3 mr-1" />
              {days} day{days !== 1 ? 's' : ''} left
            </Badge>
          ) : null}
          {submitted && (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              You&apos;ve Submitted
            </Badge>
          )}
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" data-testid="opp-title">
          {opp.title}
        </h1>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground mb-6">
          <span className="flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            Sponsored by <span className="font-medium text-foreground">{opp.sponsor_institution}</span>
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Deadline: <span className="font-medium text-foreground">{opp.deadline}</span>
          </span>
        </div>

        <Card className="mb-6 animate-fade-in">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2">About this opportunity</h3>
            <p className="text-sm sm:text-base text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {opp.description}
            </p>
          </CardContent>
        </Card>

        {/* Action zone */}
        <Card className="border-primary/40 bg-primary/5 animate-fade-in">
          <CardContent className="p-6">
            {isExpired ? (
              <div className="text-center py-2 text-muted-foreground">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>This opportunity has closed. Proposals are no longer accepted.</p>
              </div>
            ) : showExpertGate ? (
              <div className="text-center">
                <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h3 className="font-semibold text-lg mb-1">Sign in as an Expert to participate</h3>
                <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
                  {isIndustry
                    ? 'This opportunity is for experts/researchers. Please sign in with an expert account to download the Call PDF and submit your proposal.'
                    : 'Only verified experts can download the Call PDF and submit proposals. Sign in with LinkedIn to continue.'}
                </p>
                <Button
                  onClick={goSignInAsExpert}
                  size="lg"
                  className="gap-2"
                  data-testid="signin-expert-btn"
                >
                  <Linkedin className="w-4 h-4" />
                  Sign in as Expert
                </Button>
              </div>
            ) : !isApproved ? (
              <div className="text-center">
                <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-amber-500" />
                <h3 className="font-semibold text-lg mb-1">Profile pending approval</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  Complete your expert profile and meet the three qualification criteria to download
                  the Call PDF and submit a proposal.
                </p>
                <Link to="/expert/complete-profile">
                  <Button>Complete Profile</Button>
                </Link>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 items-center justify-center sm:justify-start">
                {opp.has_pdf && (
                  <Button
                    onClick={downloadPdf}
                    variant="outline"
                    className="gap-2"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download Call PDF
                  </Button>
                )}
                <label className="inline-flex">
                  <Button
                    asChild
                    className="gap-2 cursor-pointer"
                    disabled={uploading}
                  >
                    <span>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {submitted ? 'Replace Proposal' : 'Submit Proposal (PDF)'}
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => submitProposal(e.target.files?.[0])}
                    data-testid="submit-proposal-input"
                  />
                </label>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
